// Room (UI-S2, UI-P1): one header row (room, mode, overlapping seat dots, the two-part language chip - speak | read,
// D-34, MAJLIS-44), the live canvas, and the bottom bar with the one primary action (mute). Sheets hang from the top.
// MAJLIS-137 (D-57, UI-C39; the owner-approved passive-mode mock-up, frames (c)/(d)): a PASSIVE room is today's screen
// minus the code, the invite, the seat dots and count, the Listen button and the "I speak" half of the chip, with
// "Passive" where the code was. Its phrases are drawn as other people's (start side, "Voice n", Voice offered). Next to
// Mute, the "Auto-play translations" switch (off by default); the first translation that lands while it is off shows
// the hint "Translations are silent..." in the mute line's slot, once per session, dismissable.
// MAJLIS-163 (UI-S2/UI-C8, UI-C10, UI-C14 amended; the owner-approved room-controls mock-up): the code and the mode line
// are ONE title button with a chevron that opens the Majlis sheet (the invite link for everyone; under "Host only" the
// room mode, the lock, the room list and End majlis); the seat dots sit in a light pill and open People (the seat sheet
// renamed: who waits, who is in, the invite link). Leave: a host is asked (Leave, or End majlis for everyone); a guest
// leaves at once to Rejoin (UI-C35); a passive room keeps MAJLIS-137's rule (Leave ends) - MAJLIS-166: through the same
// Leave sheet, with its one red row. Every destructive confirmation is a ConfirmSheet (an overlay), never an inline block.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import type { Clock } from "../clock";
import {
  LEVEL_TICK_MS,
  LISTEN_HINT_MS,
  MODE_NOTICE_MS,
  ROOM_NAME_MAX,
  SPEAK_NOTICE_MS,
  SPEAK_REPLY_TIMEOUT_MS,
  WAVE_BARS,
  WRITING_AGAIN_MS,
} from "../constants";
import { languageName, sameLanguage } from "../data/languages";
import { READ_LANGUAGES } from "../data/readLanguages";
import { SPEAK_LANGUAGES } from "../data/speakLanguages";
import { dirOf, plural, useI18n } from "../i18n";
import { primeAudio, type MicOpener, type MicSource } from "../media/mic";
import { documentPage, MicKeeper, type KeeperPage } from "../media/micKeeper";
import type { MicGate } from "../media/micGate";
import type { ReplayControl } from "../media/replay";
import type { VoiceQueue } from "../media/voiceQueue";
import type { LiveUnder } from "../prefs";
import type { Knock, Listing, ListenMode } from "../protocol/types";
import { MicRing, type MicPermission, type RingState } from "../media/micRing";
import type { CreatedRoom } from "../protocol/client";
import { born, bubbleView, needsTicks, nextBoundary } from "../room/liveBubble";
import {
  compareView,
  comparePartners,
  latestSpeaker,
  nextCompareBoundary,
  nextTranslationBoundary,
  orderedBubbles,
  presentSeats,
  speakingSeats,
  translationView,
  type RoomModel,
  type SeatView,
} from "../room/model";
import { BottomBar } from "./BottomBar";
import { BehindMark, ListenSheet } from "./Listen";
import { Bubble, speakerLabel } from "./Bubble";
import { NameVoiceSheet } from "./NameVoiceSheet";
import { EngineStatusBanner, JumpToLivePill, ModeNotice, ReconnectBanner, SpeakNotice, TranscriptCanvas } from "./Canvas";
import { ChevronEndIcon, CloseIcon, HangUpIcon, LeaveIcon, LinkIcon, ListIcon, MicIcon } from "./icons";
import { LanguagePicker } from "./LanguagePicker";
import { ReplayAnnouncer } from "./ReplayButton";
import { SeatChip, SeatDot } from "./Seat";
import { Sheet } from "./Sheet";
import { ConfirmSheet } from "./ConfirmSheet";
import { KnockCards, LockIcon, WaitingList } from "./Knocks";
import { CompareBanner, ComparePill, CompareSlot } from "./Compare";

export interface RoomScreenProps {
  model: RoomModel;
  code: string;
  /** MAJLIS-126 (ruling 6): the room's name, shown where the code sits when this seat was never given the code (it came
   * in by a Listed open room's handle, or by its room id from Your rooms). */
  title?: string;
  /** MAJLIS-126 (P-36, UI-C36): the host lists the room or unlists it; room_updated is what moves the radios. */
  onListing?: (listing: Listing, name?: string) => void;
  reconnecting: boolean;
  offline: boolean;
  readLang: string;
  onReadLang: (tag: string) => void;
  muted: boolean;
  onMute: () => void;
  listening: boolean;
  onListen: () => void;
  /** MAJLIS-62 ruling 2: a remote voice waits for a tap before it can play (the app's Hearing says so). */
  tapToHear: boolean;
  onLeave: () => void;
  clock: Clock;
  openMic: MicOpener;
  /** A-3, TS-3: the one microphone track, for publishing (null when closed, denied, no device, or ended). */
  onMicTrack?: (track: MediaStreamTrack | null) => void;
  /** MAJLIS-71 (D-52): set the seat's mute with the EXISTING P-3 messages for the microphone keeper - true when the
   * track ENDED while unmuted, false when a re-open brought it back; `tap` is true for the mic tap's own unmute. */
  onMicHold?: (muted: boolean, tap: boolean) => void;
  /** MAJLIS-71: the page's visibility for the silent re-open (the document unless a test injects one). */
  page?: KeeperPage;
  reduced: boolean;
  /** MAJLIS-109 (UI-C9, D-34): below LANG_CHIP_COMPACT_BELOW_PX (constants.ts) each half of the two-part chip
   * shows its BCP-47 code instead of its full name, so the seat-stack does not squeeze under a sensible width
   * (MIN_SEAT_STACK_PX). The accessible name (aria-label, below) carries the full name either way. */
  chipCompact: boolean;
  created: CreatedRoom | null;
  /** UI-C5: the canvas's one replay player. */
  replay: ReplayControl;
  /** EM-25: the one owner of the microphone's enabled flag (this seat's mute AND the replay gate). */
  micGate: MicGate;
  /** MAJLIS-63 (UI-C17): joined/resumed said this seat is the room's host; only the host is shown the mode switch. */
  host?: boolean;
  /** MAJLIS-63 (P-17): the host asks for the room's mode; audio_mode_changed is what changes what this shows. */
  onAudioMode?: (mode: "live" | "screen_only") => void;
  /** MAJLIS-64 (D-43): the phone's voice queue - what the room shows of it (UI-C6, UI-C22, UI-C23) and asks of it. */
  voice?: VoiceControl;
  /** MAJLIS-64 (UI-C21): the person chose how to listen (set_listen_mode). */
  onListenMode?: (mode: ListenMode) => void;
  /** MAJLIS-64 (UI-C21, D-43(2)): the personal setting for live voices under a spoken phrase, and its change. */
  liveUnder?: LiveUnder;
  onLiveUnder?: (v: LiveUnder) => void;
  /** iOS/iPadOS: element volume is ignored, so live voices are MUTED under a voice and the setting is not offered. */
  ios?: boolean;
  /** MAJLIS-80 (UI-C14, ruling b): the join link a HOST can show and share when this device did not create the room
   * (it opened the host link, or reloaded): built from the code the server gave the host. Never the host link. */
  hostJoinUrl?: string | null;
  /** MAJLIS-80 (P-14 close_room, UI-C14): the host ends the majlis, after a confirmation step. */
  onEndMajlis?: () => void;
  /** MAJLIS-80 (P-14 remove_seat, UI-C14): the host removes one seat, after a confirmation step. */
  onRemoveSeat?: (seatId: string) => void;
  /** MAJLIS-65 (P-16 name_voice, UI-C16): the shared seat's holder or a host names one of its voices. */
  onNameVoice?: (voiceId: string, name: string) => void;
  /** MAJLIS-44 (P-17b, D-34): THIS seat asks to speak another language; returns whether the request was sent. Only
   * seat_updated changes what the room shows (never optimistic). */
  onSpeakLang?: (tag: string) => boolean;
  /** MAJLIS-44: how many speak_lang_refused this seat has received; each new one is the picker's refusal line. */
  speakRefusals?: number;
  /** MAJLIS-37 (P-24, UI-C30): the host asks to lock or unlock the door; room_updated is what moves the switch. */
  onLock?: (locked: boolean) => void;
  /** MAJLIS-37 (P-25, UI-C31): the knocks waiting, as the server last listed them to this HOST (empty for a guest). */
  knocks?: Knock[];
  onAnswerKnock?: (identityId: string, admit: boolean) => void;
  /** MAJLIS-137 (UI-C39): a passive room's "Auto-play translations" switch (this phone's; off by default) and its tap. */
  autoPlay?: boolean;
  onAutoPlay?: () => void;
  /** MAJLIS-140 (D-59, condition 5): the passive voice track could not play a clip - the switch shows its line. */
  playbackUnavailable?: boolean;
  /** MAJLIS-163 (UI-C14): the join link the Majlis and People sheets offer EVERY seat ("Invite people with this link"):
   * the created room's, or one built from the code this seat holds; null when it holds none (it came in by a name). */
  joinUrl?: string | null;
  /** MAJLIS-163 (UI-C14): wall ms the majlis started, for the Majlis sheet's "Started <time>" - Your rooms' record of
   * this room (the server's `created`, else this phone's first visit, MAJLIS-145); null: not known, no line. */
  startedAt?: number | null;
}

export type VoiceControl = Pick<
  VoiceQueue,
  "subscribe" | "version" | "speaking" | "pending" | "behind" | "catchUp" | "tapVoice" | "autoPlay"
>;

const NO_VOICE: VoiceControl = {
  subscribe: () => () => undefined,
  version: () => 0,
  speaking: () => null,
  pending: () => null,
  behind: () => 0,
  catchUp: () => undefined,
  tapVoice: () => undefined,
  autoPlay: () => false,
};

export function RoomScreen(p: RoomScreenProps) {
  const { lang, t } = useI18n();
  const { model, clock } = p;
  const [tickNow, setNow] = useState(() => clock.now());
  const now = Math.max(tickNow, model.clockAt); // events move the stages too
  // MAJLIS-163: "people" is the seat sheet renamed; "majlis" the title's sheet; "leave" the host's Leave question
  const [sheet, setSheet] = useState<"people" | "majlis" | "leave" | "read" | "speak" | "listen" | null>(null);
  // MAJLIS-80 (UI-C14): the destructive host action waiting for its confirmation, if any (never sent before it).
  // MAJLIS-166: while it is set, its ConfirmSheet is the one sheet open; Cancel goes back to the sheet it came from.
  const [confirm, setConfirm] = useState<{ kind: "end" } | { kind: "remove"; seatId: string } | null>(null);
  // MAJLIS-65 (UI-C16): the voice whose NameVoiceSheet is open (its id), if any
  const [naming, setNaming] = useState<string | null>(null);
  const [pinned, setPinned] = useState(true);
  const [landedAtUnpin, setLandedAtUnpin] = useState(0);
  const [permission, setPermission] = useState<MicPermission>("pending");
  const [level, setLevel] = useState(0);
  const [tickRing, setTickRing] = useState<RingState>("idle");
  const mic = useRef<MicSource | null>(null);
  const levels = useRef<number[]>([]);
  const [wave, setWave] = useState<number[]>([]);
  const ring = useRef(new MicRing());
  const gate = p.micGate;
  const jump = useRef<(() => void) | null>(null);

  const me = model.mySeatId;
  const passive = model.passive; // MAJLIS-137
  const speaking = useMemo(() => speakingSeats(model), [model]);
  const serverHears = me !== null && speaking.has(me);
  const engineDown = model.engine === "down";

  // UI-C20 (consultant 2026-09-19): "Writing again" is a confirmation. It shows only when THIS client has seen the
  // engine down since it last saw it up (it showed the paused banner, or would have but for the socket banner), and
  // then learns "up", whichever message carries it (engine_status or a resume's room_state). A client that never saw
  // "down" (joined or resumed into an up room, or the whole outage passed while its socket was down) shows nothing.
  // It lasts WRITING_AGAIN_MS on the injected clock, then the slot clears.
  const [writingAgain, setWritingAgain] = useState(false);
  const [seenDown, setSeenDown] = useState(model.engine === "down");
  if (model.engine === "down" && !seenDown) {
    setSeenDown(true);
    setWritingAgain(false); // down again inside the 2 s: the next "up" starts a fresh confirmation
  } else if (model.engine === "up" && seenDown) {
    setSeenDown(false);
    setWritingAgain(true);
  }
  useEffect(() => {
    if (!writingAgain) return;
    const id = clock.setTimeout(() => setWritingAgain(false), WRITING_AGAIN_MS);
    return () => clock.clearTimeout(id);
  }, [writingAgain, clock]);

  // MAJLIS-63 (UI-C17, consultant ruling 5): every audio_mode_changed this client applies shows ONE line in the one
  // banner slot for MODE_NOTICE_MS on the injected clock, then the slot clears. It ranks AFTER the socket and engine
  // banners: while one of those holds the slot the notice is not shown, and its time still runs. The header's mode
  // label is the persistent state; this is only the moment of change.
  const [notice, setNotice] = useState<{ n: number; mode: "live" | "screen_only" } | null>(null);
  const [seenChanges, setSeenChanges] = useState(model.modeChanges);
  if (model.modeChanges !== seenChanges) {
    setSeenChanges(model.modeChanges);
    setNotice({ n: model.modeChanges, mode: model.audioMode });
  }
  useEffect(() => {
    if (!notice) return;
    const id = clock.setTimeout(() => setNotice(null), MODE_NOTICE_MS);
    return () => clock.clearTimeout(id);
  }, [notice, clock]);

  // MAJLIS-44 (D-34, P-17b; the approved mock-up's frames 2b-2e, 3a): the language THIS seat speaks, changed from the
  // header's speak half or its own row in the seat list. NEVER OPTIMISTIC: the check, the chip and the seat list show
  // only what the server recorded (seat_updated), as UI-C17 does. Between the tap and the answer the chosen row says
  // "Changing…" and the sheet stays open; the last tap wins. A refusal (speak_lang_refused, or no answer within
  // SPEAK_REPLY_TIMEOUT_MS, an ASSUMPTION) leaves everything as it was and says so.
  const mySeat = me !== null ? model.seats[me] : undefined;
  const mySpeak = mySeat?.speak_lang ?? "";
  const [speakAsk, setSpeakAsk] = useState<{ tag: string; n: number } | null>(null);
  const [speakError, setSpeakError] = useState(false);
  const [seenRefusals, setSeenRefusals] = useState(p.speakRefusals ?? 0);
  if ((p.speakRefusals ?? 0) !== seenRefusals) {
    setSeenRefusals(p.speakRefusals ?? 0);
    if (speakAsk) {
      setSpeakAsk(null);
      setSpeakError(true);
    }
  }
  useEffect(() => {
    if (!speakAsk) return;
    const id = clock.setTimeout(() => {
      setSpeakAsk(null);
      setSpeakError(true);
    }, SPEAK_REPLY_TIMEOUT_MS);
    return () => clock.clearTimeout(id);
  }, [speakAsk, clock]);
  // Every confirmed change of ANY seat's speaking language shows one line in the banner slot (lowest rank) for
  // SPEAK_NOTICE_MS (ASSUMPTION); this seat's own also closes its picker when it is the language asked for.
  const [speakNotice, setSpeakNotice] = useState<{ n: number; seat: string; lang: string } | null>(null);
  const [seenSpeak, setSeenSpeak] = useState(model.speakChange?.n ?? 0);
  if (model.speakChange && model.speakChange.n !== seenSpeak) {
    const c = model.speakChange;
    setSeenSpeak(c.n);
    setSpeakNotice(c);
    if (c.seat === me && speakAsk && speakAsk.tag === c.lang) {
      setSpeakAsk(null);
      setSpeakError(false);
      if (sheet === "speak") setSheet(null);
    }
  }
  useEffect(() => {
    if (!speakNotice) return;
    const id = clock.setTimeout(() => setSpeakNotice(null), SPEAK_NOTICE_MS);
    return () => clock.clearTimeout(id);
  }, [speakNotice, clock]);
  const speakAsks = useRef(0);
  const openSpeak = () => {
    setSpeakError(false);
    setConfirm(null);
    setSheet("speak");
  };
  const pickSpeak = (tag: string) => {
    // No confirm step: choosing a row IS the change, and choosing back is the undo. The language the seat already
    // speaks, with nothing asked, changes nothing.
    if (tag === mySpeak && !speakAsk) return setSheet(null);
    setSpeakError(false);
    if (!(p.onSpeakLang?.(tag) ?? false)) {
      setSpeakAsk(null);
      setSpeakError(true); // not sent (the socket is not live): the same line, and nothing changed
      return;
    }
    speakAsks.current += 1;
    setSpeakAsk({ tag, n: speakAsks.current });
  };

  // the local microphone: ONE stream for the ring and for publishing (TS-3; iOS must not open it twice)
  // MAJLIS-71 (D-52): opened once per room and AGAIN only when its track has ENDED, always through the one opener, the
  // old track stopped first (media/micKeeper.ts holds the state machine; this wires it to the gate, the publisher and
  // the seat's mute).
  const onMicTrack = useRef(p.onMicTrack);
  const onMicHold = useRef(p.onMicHold);
  useEffect(() => {
    onMicTrack.current = p.onMicTrack;
    onMicHold.current = p.onMicHold;
  });
  // what the room shows of the keeper, copied out of it on every change it reports (never read from the ref in render)
  const [micView, setMicView] = useState({ held: false, stopped: false });
  const keeper = useRef<MicKeeper | null>(null);
  useEffect(() => {
    const k = new MicKeeper({
      open: p.openMic,
      clock,
      page: p.page ?? documentPage(),
      prime: primeAudio,
      events: {
        source: (source, perm) => {
          mic.current = source;
          setPermission(perm);
          p.micGate.attach(source); // EM-25: from here the gate owns track.enabled - BEFORE the publisher has it
          onMicTrack.current?.(source?.track ?? null);
        },
        hold: (muted) => onMicHold.current?.(muted, false),
        changed: () => setMicView({ held: k.holding(), stopped: k.stopped() }),
      },
    });
    keeper.current = k;
    k.start();
    return () => {
      onMicTrack.current?.(null);
      p.micGate.attach(null);
      k.close();
      mic.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one keeper per room
  }, []);
  // his own mute, as the seat records it (the keeper ignores the mute it asked for itself)
  useEffect(() => {
    keeper.current?.setMuted(p.muted);
  }, [p.muted]);
  /** His OWN mute: the seat's mute unless the keeper holds it because the microphone stopped. */
  const ownMute = p.muted && !micView.held;
  const micStopped = micView.stopped;
  // UI-C10 (D-52): in the stopped state (and after a track ended while he was muted) the tap re-opens the microphone
  // and unmutes in the SAME gesture: getUserMedia is called before this handler returns. There is no mute from
  // `stopped`: a tap while a tap's own re-open is still out does nothing.
  const onMicTap = () => {
    const kk = keeper.current;
    if (kk?.needsTap()) {
      kk.tap();
      onMicHold.current?.(false, true);
      return;
    }
    if (kk?.stopped()) return;
    p.onMute();
  };

  // D-38, consultant ruling 1: the "live audio paused" hint follows the replay gate itself - told on every change,
  // so it appears when the gate closes and goes on every path the gate opens on (never a tick late). Shown only
  // where there IS live audio to pause: a live-audio room whose seat is listening.
  // MAJLIS-64: only a REPLAY's hold says "live audio paused" - a spoken translation lowers the live voices instead
  // (D-43(2)), and its own hint (UI-C24) is built with the interpreter UI.
  const watchGate = useCallback((changed: () => void) => gate.watchKinds(() => changed()), [gate]);
  const replaying = useSyncExternalStore(watchGate, () => gate.kinds().replay);
  const livePaused = replaying && p.listening && model.audioMode === "live";
  // MAJLIS-64 (UI-C24): a spoken translation holds the same gate with kind `voice` (tail included)
  const voicing = useSyncExternalStore(watchGate, () => gate.kinds().voice);

  // MAJLIS-64: the voice queue, read on every change it reports (UI-C6, UI-C22, UI-C23)
  const vq = p.voice ?? NO_VOICE;
  useSyncExternalStore(vq.subscribe, vq.version);
  // Nothing voice-related shows when voicing is off for this deployment (room_state.voice false): no affordance that
  // cannot work. Tap Voice (UI-C6) needs only that; interpreter mode (UI-C21..C23) also needs auto-play in this build
  // and a live-audio room, and is offered only where there is something to translate - a present seat that speaks
  // another language than this reader reads ("own-language rooms show the toggle unchanged", UI-C21).
  const voiceOn = model.voiceOn;
  const liveRoom = model.audioMode === "live";
  const others = presentSeats(model).filter((s) => s.id !== model.mySeatId);
  const translating = others.some((s) => !sameLanguage(s.speak_lang, p.readLang));
  const interpreter = voiceOn && vq.autoPlay() && liveRoom && translating;
  const behind = voiceOn ? vq.behind() : 0;
  const underState: LiveUnder = p.ios ? "muted" : (p.liveUnder ?? "lowered");

  // UI-C29 ListenActivity (MAJLIS-88, the owner-approved mock-up): while Listen is OFF in a live-audio room and any
  // OTHER seat has a phrase open (speech_start until speech_end: the same span its chip glows, UI-C7), the Listen
  // control shows activity in the speaking seat's colour - the most recently opened phrase's seat when several speak
  // at once. Nothing at all with Listen on, in a screen-only room, for this seat's own voice, or while this phone
  // replays a phrase or plays a translation (the gate's replay / voice holds). It follows the model, so it stops on
  // the very render that closes the phrase (the row's 500 ms is a bound, not a delay).
  const heardNow = useMemo(() => {
    let best: (typeof model.bubbles)[string] | null = null;
    for (const b of Object.values(model.bubbles))
      if (b.phase === "speaking" && b.seat !== me && model.seats[b.seat] && (!best || b.t0 >= best.t0)) best = b;
    return best;
  }, [model, me]);
  const activitySeat = heardNow ? model.seats[heardNow.seat] : undefined;
  const listenActivity =
    !p.listening && liveRoom && !replaying && !voicing && heardNow && activitySeat
      ? { name: speakerLabel(activitySeat, heardNow, t), colour: activitySeat.colour }
      : null;
  // The first time per room (per RoomScreen: this phone, this room) the activity shows, the hint shows beside it for
  // LISTEN_HINT_MS on the injected clock, and never again in this room. ASSUMPTIONS (UI-C29): 4 s, first time only.
  const [hintAt, setHintAt] = useState<number | null>(null);
  const [hintOver, setHintOver] = useState(false);
  if (listenActivity && hintAt === null) setHintAt(clock.now());
  useEffect(() => {
    if (hintAt === null) return;
    const id = clock.setTimeout(() => setHintOver(true), hintAt + LISTEN_HINT_MS - clock.now());
    return () => clock.clearTimeout(id);
  }, [hintAt, clock]);
  const listenHint = !!listenActivity && hintAt !== null && !hintOver;

  // EM-25: his mute goes to the gate, never to the track: the gate is the one writer of that flag, so a replay
  // cannot be undone by an unmute and an unmute cannot open a microphone a replay holds shut.
  useEffect(() => {
    p.micGate.setMuted(p.muted);
  }, [p.micGate, p.muted, permission]);

  // the ~9 Hz tick: local level, waveform history, live-bubble stages (UI-M5, EM-11, EM-12)
  const state = useRef({ muted: ownMute, stopped: micStopped, reconnecting: p.reconnecting, serverHears, engineDown, permission });
  useEffect(() => {
    state.current = { muted: ownMute, stopped: micStopped, reconnecting: p.reconnecting, serverHears, engineDown, permission };
  });
  const tick = useCallback(() => {
    const s = state.current;
    const lvl = mic.current && !s.muted ? mic.current.level() : 0;
    const hist = levels.current;
    hist.push(lvl);
    if (hist.length > WAVE_BARS * 2) hist.splice(0, hist.length - WAVE_BARS);
    const n = clock.now();
    setWave(hist.slice(-WAVE_BARS));
    setLevel(lvl);
    setNow(n);
    setTickRing(ring.current.update({ ...s, playing: gate.gated(), level: lvl, now: n }));
  }, [clock, gate]);
  const ticking = permission === "granted" || needsTicks(Object.values(model.bubbles));
  useEffect(() => {
    if (!ticking) return;
    const id = clock.setInterval(tick, LEVEL_TICK_MS);
    return () => clock.clearInterval(id);
  }, [ticking, tick, clock]);
  // and time alone moves them at exact boundaries (EM-11: born at 300 ms, 3 s, 10 s), and a pending translation
  // slot gives up at its own (MAJLIS-49: TRANSLATION_WAIT_MS)
  // MAJLIS-113 (UI-C33): the comparison phrases paired to this seat's bubbles (empty unless this phone compares)
  const partners = useMemo(() => comparePartners(model), [model]);
  useEffect(() => {
    const live = nextBoundary(Object.values(model.bubbles), now);
    const tr = nextTranslationBoundary(model, p.readLang, now);
    // MAJLIS-113 (UI-C34): a waiting compare slot turns into "(nothing)" at its own boundary
    const cmp = model.compare ? nextCompareBoundary(model, partners, now) : null;
    const at = [live, tr, cmp].reduce<number | null>((a, b) => (a === null ? b : b === null ? a : Math.min(a, b)), null);
    if (at === null) return;
    const id = clock.setTimeout(() => setNow(clock.now()), at - clock.now());
    return () => clock.clearTimeout(id);
  }, [model, now, clock, p.readLang, partners]);

  const ringState: RingState =
    permission === "denied"
      ? "denied"
      : permission === "unavailable"
        ? "unavailable"
        : ownMute
          ? "muted"
          : micStopped
            ? "stopped" // D-52: below his own mute, above reconnecting
            : p.reconnecting
              ? "reconnecting"
              : gate.gated()
                ? "playing" // EM-25: shown at once, not on the next tick
                : serverHears
                  ? "heard"
                  : engineDown && tickRing === "unheard"
                    ? "hearing" // UI-C20: suppressed at once, not on the next tick
                    : tickRing;

  const seats = presentSeats(model);
  // MAJLIS-166: the seat whose Remove is being confirmed, while it is still present (gone: nothing to confirm)
  const removing = confirm?.kind === "remove" ? seats.find((s) => s.id === confirm.seatId) : undefined;
  const bubbles = orderedBubbles(model)
    .map((b) => ({
      b,
      view: bubbleView(b, now),
      tr: translationView(model, b, p.readLang, now),
      cmp: compareView(model, b, partners, now), // MAJLIS-113: null on every phone but the compared seat's
    }))
    .filter((x) => x.view.visible);
  // P-19 (MAJLIS-89): where the join's earlier phrases begin and end in canvas order (-1: none shown)
  // MAJLIS-137 (UI-C39): the auto-play hint. It shows when a translation first lands on screen while the switch is OFF -
  // once per session (this RoomScreen: one join) - and goes for good when dismissed or when the switch is turned on.
  // Only where voicing is on at all (no switch is offered otherwise).
  const [autoPlayHint, setAutoPlayHint] = useState<"unseen" | "showing" | "done">("unseen");
  const translated = passive && voiceOn && bubbles.some((x) => x.tr.kind === "text");
  if (autoPlayHint === "unseen" && translated && !p.autoPlay) setAutoPlayHint("showing");
  if (autoPlayHint === "showing" && p.autoPlay) setAutoPlayHint("done");
  const firstEarlier = bubbles.findIndex((x) => x.b.earlier === true);
  const lastEarlier = bubbles.map((x) => x.b.earlier === true).lastIndexOf(true);
  const clockOf = (ms: number) => new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(ms);
  const speaker = latestSpeaker(model);
  const fresh = pinned ? 0 : model.landedSeq - landedAtUnpin;
  // MAJLIS-65 (UI-C16, P-16): who may name a voice - the HOLDER of that shared seat (this device's own seat) or a HOST.
  // The server enforces the same rule (not_host otherwise); this only decides whether the label is offered as a button.
  const mayName = (seat: SeatView | undefined) => !!seat && seat.kind === "shared" && (seat.id === me || !!p.host);
  // The open NameVoiceSheet's voice, read from the newest bubble of it (so its label is always the current one). It
  // closes by itself if the reader loses the right to name it.
  const namingBubble =
    naming === null
      ? undefined
      : orderedBubbles(model)
          .reverse()
          .find((b) => b.segment?.voice?.id === naming && mayName(model.seats[b.seat]));

  const onPinnedChange = useCallback(
    (v: boolean) => {
      setPinned(v);
      if (!v) setLandedAtUnpin(model.landedSeq);
    },
    [model.landedSeq],
  );

  const knocks = p.knocks ?? [];
  const answerKnock = (identityId: string, admit: boolean) => p.onAnswerKnock?.(identityId, admit);

  // MAJLIS-163: what names this majlis in the title button and its sheets - the code, else (no code held) its name
  const roomLabel = p.code || p.title || "";
  const readName = languageName(p.readLang, lang);
  const readNative = READ_LANGUAGES.find((l) => l.tag === p.readLang)?.native ?? readName;
  const speakName = languageName(mySpeak, lang);
  const speakNative = SPEAK_LANGUAGES.find((l) => l.tag === mySpeak)?.native ?? speakName;
  const pulse = speakNotice !== null && speakNotice.seat === me;
  // MAJLIS-109 (UI-C9): below the bound each half shows its BCP-47 tag, upper-cased, in place of its name.
  // ASSUMPTION: codes stay LTR and upper-case even in the Arabic interface - they are identifiers, not
  // translated text (like a currency or a flight code), and reversing or Arabic-izing them would make the
  // one thing they are FOR (a quick, internationally readable label) less readable, not more. The accessible
  // name (aria-label above) is the full language name either way, so nothing here can change what is announced.
  const speakDisplay = p.chipCompact ? mySpeak.toUpperCase() : speakNative;
  const readDisplay = p.chipCompact ? p.readLang.toUpperCase() : readNative;
  const speakDisplayDir = p.chipCompact ? "ltr" : dirOf(mySpeak);
  const readDisplayDir = p.chipCompact ? "ltr" : dirOf(p.readLang);

  return (
    <div className="room" data-spec="UI-S2 UI-P1 UI-P6">
      <header className="room-header">
        {passive ? (
          <div className="room-id">
            {/* MAJLIS-137 (UI-C39): "Passive" where the code was; no mode line (there is nobody to hear or lock out) */}
            <span className="room-code" dir="auto" data-spec="UI-C39 D-57">
              {t("modePassive")}
            </span>
          </div>
        ) : (
        <button
          type="button"
          className="room-title"
          aria-label={t("majlisSettings", { code: roomLabel })}
          aria-haspopup="dialog"
          onClick={() => {
            setConfirm(null);
            setSheet("majlis");
          }}
          data-spec="UI-S2 UI-C14"
        >
        <span className="room-id">
          <span className="room-code" dir={p.code ? "ltr" : "auto"}>
            {p.code || p.title}
            {model.locked && (
              <span className="lock-mark" role="img" aria-label={t("locked")} data-spec="UI-C30 UI-C14 D-30">
                <LockIcon />
              </span>
            )}
          </span>
          <span className="room-mode" id="mode" data-mode={model.audioMode} data-spec="UI-C17 D-22 UI-C30 UI-S2">
            {/* MAJLIS-167 (item 6): the mode word is never cut - the ellipsis takes only what follows it */}
            <span className="mode-word">{model.audioMode === "live" ? t("modeLive") : t("modeScreen")}</span>
            <span className="mode-rest">
            {model.locked ? ` · ${t("locked")}` : ""}
            {model.listing !== "unlisted" && (
              <>
                {" · "}
                <span className="listed-mark" data-spec="UI-C36 D-55">
                  {t("listedMark")}
                </span>
              </>
            )}
            {/* MAJLIS-163 (the consultant, 2026-10-04): the count LAST - mode · Locked · Listed · N people - so the
                ellipsis of a narrow header eats the count first, never the lock or the listing */}
            {` · ${plural(lang, t, "peopleCount", seats.length)}`}
            </span>
          </span>
        </span>
        <ChevronEndIcon />
        </button>
        )}
        {!passive && (
        <button
          type="button"
          className="seat-stack"
          aria-label={t("people", { n: seats.length })}
          aria-haspopup="dialog"
          onClick={() => setSheet("people")}
          data-spec="UI-C8 UI-C7 P-4 D-13"
        >
          {seats.map((s) => (
            <SeatChip key={s.id} seat={s} speaking={speaking.has(s.id)} level={s.id === me ? level : undefined} />
          ))}
        </button>
        )}
        {model.compare && <ComparePill />}
        {/* D-34 (MAJLIS-44): one pill in two halves, speak | read. The speak half (with a small mic, so the halves
            cannot be confused) opens the speak picker; the read half is UI-C9's reading chip, unchanged. Each half is
            its own button with its own accessible name and a 44 x 44 px hit area. */}
        <div className="lang-pair" data-spec="UI-C9 D-34 D-33 UI-C8" data-chip-mode={p.chipCompact ? "code" : "name"}>
          {mySeat && !passive && (
            <button
              type="button"
              className={pulse ? "speak-chip pulse" : "speak-chip"}
              id="speakchip"
              aria-label={t("speakChip", { lang: speakName })}
              onClick={openSpeak}
              data-spec="D-34 D-33 P-17b"
            >
              <MicIcon />
              <span className="chip-text" lang={mySpeak} dir={speakDisplayDir}>
                {speakDisplay}
              </span>
            </button>
          )}
          <button
            type="button"
            className="reading-chip"
            id="langchip"
            aria-label={t("readingChip", { lang: readName })}
            onClick={() => setSheet("read")}
            data-spec="UI-C9 D-06 D-21"
          >
            <span className="chip-text" lang={p.readLang} dir={readDisplayDir}>
              {readDisplay}
            </span>
          </button>
        </div>
      </header>

      {/* one slot, one message: a reconnecting socket (UI-C12) outranks paused writing (UI-C20), whose state is
          unknown while the socket is down; the writing banner returns once the socket is back if still down */}
      {p.reconnecting ? (
        <ReconnectBanner offline={p.offline} />
      ) : engineDown ? (
        <EngineStatusBanner state="paused" />
      ) : writingAgain ? (
        <EngineStatusBanner state="again" />
      ) : notice ? (
        <ModeNotice key={notice.n} mode={notice.mode} />
      ) : speakNotice ? (
        <SpeakNotice
          key={speakNotice.n}
          own={speakNotice.seat === me}
          name={model.seats[speakNotice.seat]?.name ?? ""}
          lang={languageName(speakNotice.lang, lang)}
        />
      ) : model.compare ? (
        <CompareBanner /> // MAJLIS-113 (UI-C33): the lowest rank - any other line outranks it while it shows
      ) : null}

      {/* MAJLIS-37 (UI-C31): the host's knock cards sit BELOW the one banner slot and never displace it */}
      {p.host && knocks.length > 0 && (
        <KnockCards knocks={knocks} onAnswer={answerKnock} onMore={() => setSheet("people")} />
      )}

      <TranscriptCanvas
        empty={bubbles.length === 0 && !p.created && !p.hostJoinUrl}
        onPinnedChange={onPinnedChange}
        jumpRef={jump}
        contentKey={bubbles
          .map(
            (x) =>
              `${x.b.id}:${x.view.stage}:${x.b.revisionCount}:${x.tr.kind}:${x.tr.kind === "text" ? x.tr.lang : ""}:${x.cmp?.kind ?? ""}`,
          )
          .join("|")}
      >
        {p.created ? (
          <InviteCard joinUrl={p.created.join_url} hostLink={p.created.host_link} />
        ) : p.hostJoinUrl ? (
          <InviteCard joinUrl={p.hostJoinUrl} hostLink={null} />
        ) : null}
        {bubbles.map(({ b, view, tr, cmp }, i) => (
          <Fragment key={b.id}>
          {/* P-19 (MAJLIS-89): the marker above the phrases the join was handed, the divider where live begins */}
          {model.earlier && i === firstEarlier && (
            <p className="earlier-top" data-spec="MAJLIS-89 P-19 D-45">
              {plural(lang, t, "earlierMarker", model.earlier.n)}
            </p>
          )}
          <Bubble
            bubble={b}
            seat={model.seats[b.seat]}
            view={view}
            translation={tr}
            isMe={b.seat === me && !passive}
            passive={passive}
            levels={b.seat === me && b.phase === "speaking" ? wave : null}
            reduced={p.reduced}
            fromLive={b.hadLive && born(b, b.endedAt ?? now)}
            replay={p.replay}
            onNameVoice={
              b.segment?.voice && mayName(model.seats[b.seat]) ? () => setNaming(b.segment!.voice!.id) : undefined
            }
            voice={
              voiceOn
                ? {
                    // G6: a seat's own phrases are never voiced back to it - MAJLIS-137: except in a passive room
                    offered: passive || b.seat !== me,
                    spoken: vq.speaking() === b.id,
                    loading: vq.pending() === b.id && vq.speaking() !== b.id,
                    onTap: () => {
                      if (tr.kind === "text") vq.tapVoice(b.id, tr.lang);
                    },
                  }
                : undefined
            }
          />
          {cmp && <CompareSlot view={cmp} bubbleId={b.id} />}
          {model.earlier && i === lastEarlier && (
            <div className="live-divider" role="separator" data-spec="MAJLIS-89 P-19 UI-C3">
              <span>{t("liveHere", { t: clockOf(model.earlier.at) })}</span>
            </div>
          )}
          </Fragment>
        ))}
        {interpreter && behind > 0 && <BehindMark n={behind} onCatchUp={() => vq.catchUp()} />}
      </TranscriptCanvas>
      <ReplayAnnouncer replay={p.replay} />

      {!pinned && (
        <JumpToLivePill
          speaker={speaker ? speaker.name : null}
          fresh={fresh}
          onJump={() => {
            jump.current?.();
          }}
        />
      )}

      <BottomBar
        ring={ringState}
        level={level}
        muted={ownMute}
        onMute={onMicTap}
        listening={p.listening}
        listenEnabled={model.audioMode === "live"}
        livePaused={livePaused}
        tapToHear={p.tapToHear}
        onListen={p.onListen}
        listenMode={interpreter ? model.listenMode : null}
        onListenChoice={() => setSheet("listen")}
        liveUnder={underState}
        voiceCapped={voiceOn && model.voiceCapped}
        micListening={voicing && !replaying && ringState === "playing"}
        listenActivity={listenActivity}
        listenHint={listenHint}
        seatCount={seats.length}
        onSeats={() => setSheet("people")}
        // MAJLIS-137 (consultant, PR #209): in a passive room Leave ENDS the room - the passive person is the host of a
        // solo room, End lived in the seat sheet that is gone, and the record must be there at once.
        // MAJLIS-163 (ruling 2): a HOST is asked (the Leave sheet); a GUEST leaves at once, to Rejoin (UI-C35).
        // MAJLIS-166 (ruling 1): the passive host gets the SAME Leave sheet, with only its red row (Leave ends).
        onLeave={
          passive || p.host
            ? () => {
                setConfirm(null);
                setSheet("leave");
              }
            : p.onLeave
        }
        passive={passive}
        autoPlay={voiceOn ? !!p.autoPlay : null}
        onAutoPlay={p.onAutoPlay}
        autoPlayHint={autoPlayHint === "showing"}
        onDismissHint={() => setAutoPlayHint("done")}
        playbackUnavailable={passive && !!p.playbackUnavailable}
      />

      {sheet === "people" && (
        <Sheet
          title={t("people", { n: seats.length })}
          onClose={() => {
            setSheet(null);
            setConfirm(null);
          }}
          spec="UI-C10 UI-C8 D-18 UI-C14 UI-C31"
        >
          {/* MAJLIS-37 (UI-C31, 3c): host only, ABOVE the seat list - every knock waiting */}
          {p.host && <WaitingList knocks={knocks} onAnswer={answerKnock} />}
          {/* MAJLIS-163 (ruling 5): the list under its own section title, as in frame (d) */}
          <p className="host-title people-title">{t("inMajlis")}</p>
          <ul className="seat-list">
            {seats.map((s) => (
              <li key={s.id} className="seat-row">
                <SeatDot seat={s} />
                {/* MAJLIS-167 (item 7; the room-controls mock-up's frame (d), docs/spec/prototype/room-controls-mockup.html):
                    the name isolated in a <bdi>, so "· you" follows the INTERFACE's direction whatever script the
                    name is in, and "speaks ..." UNDER the name as its hint line, not mid-row */}
                <span className="row-text seat-row-text">
                  <span className="seat-row-name">
                    <bdi>{s.name}</bdi>
                    {s.id === me && <span className="you"> · {t("you")}</span>}
                  </span>
                  <span className="row-hint seat-row-meta">
                    {s.kind === "shared" ? `${t("sharedSeat")} · ` : ""}
                    {t("speaks", { lang: languageName(s.speak_lang, lang) })}
                    {s.muted ? ` · ${t("muted")}` : ""}
                  </span>
                </span>
                {/* MAJLIS-44 (the mock-up's 2f): this seat's OWN row only - nobody changes another seat's language, a
                    host included (consultant ruling 7) */}
                {s.id === me && (
                  <button
                    type="button"
                    className="text-link seat-change"
                    aria-label={t("changeSpeakNamed")}
                    onClick={openSpeak}
                    data-spec="UI-C8 D-34 P-17b"
                  >
                    {t("change")}
                  </button>
                )}
                {/* MAJLIS-80 (UI-C14): host only, never on this seat itself. Which OTHER seats are hosts the wire does
                    not say, so Remove is offered on each; the server refuses a host's seat and the app keeps quiet. */}
                {p.host && s.id !== me && (
                  <button
                    type="button"
                    className="text-link seat-remove"
                    aria-label={t("removeSeatNamed", { name: s.name })}
                    onClick={() => {
                      setSheet(null);
                      setConfirm({ kind: "remove", seatId: s.id });
                    }}
                    data-spec="UI-C14 P-14"
                  >
                    {t("removeSeat")}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {/* MAJLIS-163 (ruling 5): the invite link again at the end, so nobody has to hunt for it */}
          {p.joinUrl && <InviteRow joinUrl={p.joinUrl} />}
        </Sheet>
      )}
      {sheet === "majlis" && !passive && (
        <Sheet
          title={t("majlisTitle", { code: roomLabel })}
          sub={
            p.startedAt != null
              ? t(p.host ? "startedHost" : "started", { t: clockOf(p.startedAt) })
              : p.host
                ? t("hostYou")
                : undefined
          }
          onClose={() => {
            setSheet(null);
            setConfirm(null);
          }}
          spec="UI-C14 UI-S2 UI-C17 UI-C30 UI-C36"
        >
          {/* MAJLIS-163 (ruling 4): everyone - the existing share/copy */}
          {p.joinUrl && <InviteRow joinUrl={p.joinUrl} showUrl />}
          {p.host && (
            <RoomModeSwitch mode={model.audioMode} onChange={(mode) => p.onAudioMode?.(mode)}>
              <LockSwitch locked={model.locked} onChange={(locked) => p.onLock?.(locked)} />
              <RoomListControl
                listing={model.listing}
                name={model.roomName}
                locked={model.locked}
                defaultName={t("defaultRoomName", { name: mySeat?.name ?? "" })}
                onChange={(listing, name) => p.onListing?.(listing, name)}
              />
              <div className="seat-row end-row">
                <button
                  type="button"
                  className="danger-row end-majlis"
                  id="endmajlis"
                  onClick={() => {
                    setSheet(null);
                    setConfirm({ kind: "end" });
                  }}
                  data-spec="UI-C14 P-14"
                >
                  <HangUpIcon />
                  <span className="danger-text">{t("endForEveryone")}</span>
                </button>
              </div>
            </RoomModeSwitch>
          )}
        </Sheet>
      )}
      {sheet === "leave" && (p.host || passive) && (
        // MAJLIS-163 (ruling 2, frames (b)/(f)): the host's two choices; the red row IS the confirmation (no second
        // dialog), Cancel sends nothing. MAJLIS-166 (ruling 1): a passive room's Leave opens this same sheet with the
        // red row alone - no "Leave" row (the passive person is the solo host; MAJLIS-137: Leave ends). A passive room
        // has no code, so its title is End's question ("End this majlis for everyone?") rather than "Leave ?".
        <Sheet
          title={passive ? t("endConfirm") : codeIn(t("leaveTitle", { code: "\u0000" }), roomLabel, !!p.code)}
          sub={t("hostYou")}
          onClose={() => setSheet(null)}
          spec={passive ? "UI-C39 UI-C10 UI-C14 P-14 D-57" : "UI-C10 UI-C14 P-3 P-14"}
        >
          <div className="leave-choices">
            {!passive && (
              <button type="button" className="sheet-choice leave-only" onClick={p.onLeave} data-spec="UI-C10 P-3">
                <LeaveIcon />
                <span className="row-text">
                  <span>{t("leave")}</span>
                  <span className="row-hint">{t("leaveHint")}</span>
                </span>
              </button>
            )}
            <button
              type="button"
              className="sheet-choice danger-row leave-end"
              onClick={() => {
                setSheet(null);
                p.onEndMajlis?.();
              }}
              data-spec="UI-C14 P-14"
            >
              <HangUpIcon />
              <span className="row-text">
                <span>{t("endForEveryone")}</span>
                <span className="row-hint">{t("endForEveryoneHint")}</span>
              </span>
            </button>
            <button type="button" className="text-link leave-cancel" onClick={() => setSheet(null)}>
              {t("cancel")}
            </button>
          </div>
        </Sheet>
      )}
      {/* MAJLIS-166 (UI-C14, rulings 2): the Majlis sheet's End and the People sheet's Remove are confirmed in a sheet
          of their own - the question, one red row, Cancel (back to the sheet it came from, nothing sent) */}
      {p.host && confirm?.kind === "end" && !passive && (
        <ConfirmSheet
          question={t("endConfirm")}
          action={t("endMajlis")}
          hint={t("endForEveryoneHint")}
          icon={<HangUpIcon />}
          spec="UI-C14 P-14"
          onConfirm={() => {
            setConfirm(null);
            p.onEndMajlis?.();
          }}
          onCancel={() => {
            setConfirm(null);
            setSheet("majlis");
          }}
        />
      )}
      {p.host && removing && confirm?.kind === "remove" && (
        <ConfirmSheet
          question={t("removeConfirm", { name: removing.name })}
          action={t("removeSeat")}
          // MAJLIS-167 (item 3): the red row carries an icon like every other ConfirmSheet's; there is no seat- or
          // person-remove icon in icons.tsx, so the close cross (never the hang-up handset: that is Leave's and End's)
          icon={<CloseIcon />}
          spec="UI-C14 P-14"
          onConfirm={() => {
            setConfirm(null);
            p.onRemoveSeat?.(confirm.seatId);
          }}
          onCancel={() => {
            setConfirm(null);
            setSheet("people");
          }}
        />
      )}
      {namingBubble && (
        <NameVoiceSheet
          key={naming}
          label={speakerLabel(model.seats[namingBubble.seat], namingBubble, t, passive)}
          current={namingBubble.segment?.voice?.display_name ?? ""}
          onName={(name) => p.onNameVoice?.(namingBubble.segment!.voice!.id, name)}
          onClose={() => setNaming(null)}
        />
      )}
      {sheet === "listen" && interpreter && (
        <ListenSheet
          mode={model.listenMode}
          onMode={(mode) => p.onListenMode?.(mode)}
          readLang={p.readLang}
          liveUnder={underState}
          onLiveUnder={p.ios ? null : (v) => p.onLiveUnder?.(v)}
          ios={!!p.ios}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === "speak" && mySeat && (
        <LanguagePicker
          title={t("speak")}
          list={SPEAK_LANGUAGES}
          value={mySpeak}
          spec="UI-C9 UI-C2 D-34 D-21 P-17b"
          notes={mySeat.kind === "shared" ? [t("speakNext"), t("speakShared")] : [t("speakNext")]}
          error={speakError ? t("errSpeakChange") : null}
          pending={speakAsk?.tag ?? null}
          pendingLabel={t("changing")}
          onPick={pickSpeak}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === "read" && (
        <LanguagePicker
          title={t("read")}
          list={READ_LANGUAGES}
          value={p.readLang}
          spec="UI-C9 UI-C2 D-06 D-21"
          onPick={(tag) => {
            p.onReadLang(tag);
            setSheet(null);
          }}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

/** UI-C17 RoomModeSwitch (MAJLIS-63): host only, in the seat sheet as the prototype has it (#modeswitch). It shows
 * ONLY the server's record (never optimistic, like Listen): a tap asks, and audio_mode_changed is what moves it - a
 * tap inside the room's interval is refused quietly, and an optimistic flip would then show the opposite of what the
 * audio server enforces. */
function RoomModeSwitch({
  mode,
  onChange,
  children,
}: {
  mode: "live" | "screen_only";
  onChange: (mode: "live" | "screen_only") => void;
  /** MAJLIS-80: the rest of the host-only section (UI-C14's End majlis). */
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const live = mode === "live";
  return (
    <section className="host-section" data-spec="UI-C17 P-17 D-22">
      <p className="host-title">{t("hostOnly")}</p>
      <div className="seat-row mode-row">
        <span>{t("modeLive")}</span>
        <button
          type="button"
          className="switch"
          id="modeswitch"
          role="switch"
          aria-checked={live}
          aria-label={t("modeLive")}
          onClick={() => onChange(live ? "screen_only" : "live")}
        />
      </div>
      {children}
    </section>
  );
}

/** UI-C30 LockSwitch (MAJLIS-37, the mock-up's 1a/1b): host only, directly under Live audio, the same switch. Like
 * RoomModeSwitch it shows ONLY the server's record (room_updated), never an optimistic flip - a change inside the
 * room's interval is refused quietly. One muted line says what the state means. */
function LockSwitch({ locked, onChange }: { locked: boolean; onChange: (locked: boolean) => void }) {
  const { t } = useI18n();
  return (
    <div className="seat-row mode-row lock-row" data-spec="UI-C30 UI-C14 D-30 D-15">
      <span className="switch-label">
        <span>{t("lockRoom")}</span>
        <span className="switch-hint">{locked ? t("lockOn") : t("lockOff")}</span>
      </span>
      <button
        type="button"
        className="switch"
        id="lockswitch"
        role="switch"
        aria-checked={locked}
        aria-label={t("lockRoom")}
        onClick={() => onChange(!locked)}
        data-spec="UI-C30 P-24"
      />
    </div>
  );
}

/** UI-C36 RoomListControl (MAJLIS-126, the approved mock-up's (d)): host only, directly under "Lock the room" - the group
 * "Room list" (role=radiogroup), three radio rows with one muted line each: Unlisted (the default), Listed open, Listed
 * with key. While LOCKED, Listed open is aria-disabled and its line says why (a locked room is never listed open,
 * D-55). While listed, the room's name (1-40 characters, ASSUMPTION; prefilled "{host}'s majlis" the first time) with
 * the privacy line under it. Like the lock it shows ONLY the server's record (room_updated), never an optimistic change:
 * a radio sends set_listing and stays where the server says. A name is sent when it changes and the field is left. */
function RoomListControl({
  listing,
  name,
  locked,
  defaultName,
  onChange,
}: {
  listing: Listing;
  name: string | null;
  locked: boolean;
  defaultName: string;
  onChange: (listing: Listing, name?: string) => void;
}) {
  const { t } = useI18n();
  const current = name ?? defaultName;
  const [draft, setDraft] = useState<string | null>(null);
  const options: Array<{ k: Listing; label: string; hint: string }> = [
    { k: "unlisted", label: t("unlisted"), hint: t("unlistedHint") },
    { k: "open", label: t("listedOpen"), hint: locked ? t("openLocked") : t("listedOpenHint") },
    { k: "key", label: t("listedKey"), hint: t("listedKeyHint") },
  ];
  const pick = (k: Listing) => {
    if (k === listing || (k === "open" && locked)) return;
    // the first listing carries the name (required then); unlisting and a change of kind keep the stored one
    onChange(k, k !== "unlisted" && name === null ? current.slice(0, ROOM_NAME_MAX) : undefined);
  };
  const commitName = () => {
    const next = (draft ?? current).trim();
    setDraft(null);
    if (next && next !== name) onChange(listing, next);
  };
  return (
    <div className="listing" role="radiogroup" aria-label={t("roomList")} data-spec="UI-C36 D-55 P-36 DM-13">
      <p className="listing-title" aria-hidden="true">
        <ListIcon />
        {t("roomList")}
      </p>
      {options.map(({ k, label, hint }) => {
        const disabled = k === "open" && locked;
        return (
          <label key={k} className={disabled ? "list-option disabled" : "list-option"} aria-disabled={disabled || undefined}>
            <input
              type="radio"
              name="room-listing"
              value={k}
              id={`listing-${k}`}
              checked={listing === k}
              aria-disabled={disabled || undefined}
              aria-describedby={`listing-${k}-hint`}
              onChange={() => pick(k)}
            />
            <span className="switch-label">
              <span>{label}</span>
              <span className="switch-hint" id={`listing-${k}-hint`}>
                {hint}
              </span>
            </span>
          </label>
        );
      })}
      {listing !== "unlisted" && (
        <div className="field listing-name">
          <label htmlFor="room-name">{t("roomName")}</label>
          <input
            id="room-name"
            className="text-input"
            dir="auto"
            maxLength={ROOM_NAME_MAX}
            value={draft ?? current}
            aria-describedby="room-name-privacy"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitName();
            }}
          />
          <span id="room-name-privacy" className="hint privacy-line">
            {t("listingPrivacy")}
          </span>
        </div>
      )}
    </div>
  );
}

/** MAJLIS-163 (UI-C14, frames (c)/(d)): "Invite people with this link" with Copy - the existing CopyRow's copy, as a
 * sheet row. The Majlis sheet shows the link under the words; the People sheet's repeat is the words alone. */
function InviteRow({ joinUrl, showUrl }: { joinUrl: string; showUrl?: boolean }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  return (
    <div className="seat-row invite-row" data-spec="UI-C14 EM-4">
      <LinkIcon />
      <span className="row-text">
        <span>{t("invite")}</span>
        {showUrl && (
          <code className="row-hint" dir="ltr">
            {joinUrl}
          </code>
        )}
      </span>
      <button
        type="button"
        className="text-link invite-copy"
        onClick={() => {
          void navigator.clipboard?.writeText(joinUrl).then(() => setDone(true));
        }}
      >
        {done ? t("copied") : t("copy")}
      </button>
    </div>
  );
}

/** MAJLIS-163: a sheet title with the room's code set inside it as its own LTR run (frame (f): «مغادرة المجلس A7K3؟»).
 * `template` carries U+0000 where the code goes. A room NAME (no code held) keeps its own direction. */
function codeIn(template: string, label: string, isCode: boolean): ReactNode {
  const [before, after = ""] = template.split("\u0000");
  return (
    <>
      {before}
      <span dir={isCode ? "ltr" : "auto"}>{label}</span>
      {after}
    </>
  );
}

/** EM-4, UI-C14: the join link to share, and - only right after creating the room - the host link, shown once. A host
 * who opened the host link (hostLink null) is shown the join link only: the host link is never re-shown. */
function InviteCard({ joinUrl, hostLink }: { joinUrl: string; hostLink: string | null }) {
  const { t } = useI18n();
  return (
    <section className="invite" data-spec="EM-4 EM-18 UI-C14">
      <CopyRow label={t("invite")} value={joinUrl} />
      {hostLink !== null && <CopyRow label={t("hostLink")} value={hostLink} />}
    </section>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  return (
    <div className="copy-row">
      <p className="copy-label">{label}</p>
      <div className="copy-line">
        <code dir="ltr">{value}</code>
        <button
          type="button"
          className="text-link"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setDone(true));
          }}
        >
          {done ? t("copied") : t("copy")}
        </button>
      </div>
    </div>
  );
}
